import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CircleAlert, Clock, Copy, FileText, Info, LifeBuoy, PackageSearch, RefreshCw } from 'lucide-react';
import OrderErrorBoundary from '../../components/OrderErrorBoundary';
import PageHeader from '../../components/site/PageHeader';
import EmptyState from '../../components/site/EmptyState';
import OrderItemsList from './components/OrderItemsList';
import DeliverableCard from './components/DeliverableCard';
import RepositoryAccess from './components/RepositoryAccess';
import OrderReviews from './components/OrderReviews';
import OrderTimeline from './components/OrderTimeline';
import AccountSettingsModal from './components/AccountSettingsModal';
import orderService from '../../services/orderService';
import invoiceService from '../../services/invoiceService';
import { openDeliverableItem } from '../../services/deliverableService';
import { applyProfileUpdate } from '../auth/state/Actions';
import { useAppDispatch } from '../../state/AppStateProvider';
import { toast } from '../../ui';
import { formatDate, formatDateTime, formatINR } from '../../utils/format';
import { ORDER_STATE, isPaidState, needsPayment, orderNumber, orderStateMeta, resolveOrderState } from './orderState';
import './style.css';

const PAYMENT_PROVIDERS = { 'manual-upi': 'UPI QR', phonepe: 'PhonePe' };

const isDatabaseId = value => /^[a-f\d]{24}$/i.test(value || '');

const PageSkeleton = () => (
  <div className="pk-split" aria-hidden="true">
    <div className="pk-stack">
      <span className="pk-skel" style={{ height: 160 }} />
      <span className="pk-skel" style={{ height: 220 }} />
    </div>
    <div className="pk-stack">
      <span className="pk-skel" style={{ height: 200 }} />
      <span className="pk-skel" style={{ height: 180 }} />
    </div>
  </div>
);

/** What the customer should know (and can do) about payment, per order state. */
const PaymentStatus = ({ order, state, refreshing, onRefresh }) => {
  const number = orderNumber(order);
  const canRefresh = state === ORDER_STATE.PAYMENT_SUBMITTED || state === ORDER_STATE.PAID;

  let body = null;
  if (needsPayment(state)) {
    body = (
      <>
        <p className="pk-order__text">
          {state === ORDER_STATE.PAYMENT_FAILED
            ? "Your last payment for this order wasn't completed. You can pay again by UPI."
            : "We haven't received payment for this order yet."}
        </p>
        <Link to={`/checkout/pay/${encodeURIComponent(number)}`} className="pk-btn pk-btn--primary pk-btn--block">
          Complete payment
        </Link>
      </>
    );
  } else if (state === ORDER_STATE.PAYMENT_SUBMITTED) {
    body = (
      <div className="pk-alert" data-tone="info">
        <Clock aria-hidden="true" />
        <span>
          <strong>Payment under review</strong> — typically processed within 12 hours. Each UPI payment is checked
          by hand.
        </span>
      </div>
    );
  } else if (state === ORDER_STATE.PAID) {
    body = <p className="pk-order__text">Payment verified. We're preparing your files and repository access.</p>;
  } else if (state === ORDER_STATE.FULFILLED) {
    body = <p className="pk-order__text">Your files and repository access are ready.</p>;
  } else if (state === ORDER_STATE.CANCELLED) {
    body = (
      <p className="pk-order__text">
        This order was cancelled.
        {order.cancellationReason ? ` ${order.cancellationReason}` : ''}
      </p>
    );
  } else if (state === ORDER_STATE.REFUNDED) {
    body = <p className="pk-order__text">This order was refunded.</p>;
  }

  return (
    <section className="pk-card" aria-labelledby="order-status-title">
      <div className="pk-card__head">
        <h2 className="pk-card__title" id="order-status-title">
          Status
        </h2>
        {canRefresh && (
          <button
            type="button"
            className="pk-btn pk-btn--ghost pk-btn--xs"
            onClick={onRefresh}
            aria-busy={refreshing || undefined}
          >
            <RefreshCw aria-hidden="true" /> Refresh
          </button>
        )}
      </div>
      <div className="pk-stack">
        {body}
        <OrderTimeline order={order} state={state} />
      </div>
    </section>
  );
};

const OrderSummary = ({ order }) => {
  const number = orderNumber(order);
  const provider = PAYMENT_PROVIDERS[String(order.paymentProvider || '').toLowerCase()];

  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(number);
      toast.success('Order number copied.');
    } catch {
      toast.error("Couldn't copy. Select the number and copy it instead.");
    }
  };

  return (
    <section className="pk-card" aria-labelledby="order-summary-title">
      <div className="pk-card__head">
        <h2 className="pk-card__title" id="order-summary-title">
          Summary
        </h2>
      </div>
      <dl className="pk-rows">
        <div>
          <dt>Order number</dt>
          <dd className="pk-order__number">
            <span>{number}</span>
            <button type="button" className="pk-order__copy" onClick={copyNumber} aria-label="Copy order number">
              <Copy aria-hidden="true" />
            </button>
          </dd>
        </div>
        <div>
          <dt>Placed</dt>
          <dd>{formatDate(order.createdAt)}</dd>
        </div>
        {provider && (
          <div>
            <dt>Payment</dt>
            <dd>{provider}</dd>
          </div>
        )}
        <div>
          <dt>Price</dt>
          <dd>{formatINR(order.subTotal)}</dd>
        </div>
        {order.feeAmount > 0 && (
          <div>
            <dt>Platform fee</dt>
            <dd>{formatINR(order.feeAmount)}</dd>
          </div>
        )}
        {order.discountAmount > 0 && (
          <div>
            <dt>Discount</dt>
            <dd>−{formatINR(order.discountAmount)}</dd>
          </div>
        )}
        {order.taxAmount > 0 && (
          <div>
            <dt>{order.pricesIncludeTax === false ? 'GST' : 'Includes GST'}</dt>
            <dd>{formatINR(order.taxAmount)}</dd>
          </div>
        )}
        <div className="pk-rows__total">
          <dt>Total</dt>
          <dd>{formatINR(order.totalAmount)}</dd>
        </div>
      </dl>
    </section>
  );
};

const OrderHelp = ({ order }) => {
  const number = orderNumber(order);
  const [opening, setOpening] = useState(false);

  const openInvoice = async () => {
    if (opening) return;
    setOpening(true);
    const result = await invoiceService.openInvoice(order.orderId);
    setOpening(false);
    if (!result.success) toast.error(result.error);
  };

  return (
    <section className="pk-card pk-card--muted" aria-labelledby="order-help-title">
      <div className="pk-card__head">
        <h2 className="pk-card__title" id="order-help-title">
          Invoice and help
        </h2>
      </div>
      <div className="pk-stack">
        {order.invoiceNumber && (
          <button
            type="button"
            className="pk-btn pk-btn--secondary pk-btn--sm pk-btn--block"
            onClick={openInvoice}
            aria-busy={opening || undefined}
          >
            <FileText aria-hidden="true" /> Invoice {order.invoiceNumber}
          </button>
        )}
        <Link
          to={`/contact?order=${encodeURIComponent(number)}`}
          className="pk-btn pk-btn--secondary pk-btn--sm pk-btn--block"
        >
          <LifeBuoy aria-hidden="true" /> Contact support
        </Link>
        <p className="pk-hint">Support hours: Monday–Friday, 9:00 AM–6:00 PM IST.</p>
      </div>
    </section>
  );
};

const OrderDetails = () => {
  const { orderId } = useParams();
  const dispatch = useAppDispatch();
  const [view, setView] = useState({ status: 'loading', order: null, error: '', notFound: false });
  const [refreshing, setRefreshing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const fetchOrder = useCallback(
    () => (isDatabaseId(orderId) ? orderService.getOrderById(orderId) : orderService.getOrderByOrderId(orderId)),
    [orderId]
  );

  const load = useCallback(async () => {
    setView({ status: 'loading', order: null, error: '', notFound: false });
    const result = await fetchOrder();
    const order = result.success ? result.data?.data : null;
    if (!order) {
      setView({
        status: 'error',
        order: null,
        error: result.error || "We couldn't load this order.",
        notFound: result.statusCode === 404 || (result.success && !order),
      });
      return;
    }
    setView({ status: 'ready', order, error: '', notFound: false });
  }, [fetchOrder]);

  // Reload in place (after a refresh or a settings change) without blanking the page.
  const refresh = useCallback(async () => {
    setRefreshing(true);
    const result = await fetchOrder();
    setRefreshing(false);
    const order = result.success ? result.data?.data : null;
    if (order) setView(v => ({ ...v, order }));
    else toast.error(result.error || "We couldn't refresh this order.");
  }, [fetchOrder]);

  useEffect(() => {
    load();
  }, [load]);

  const crumbs = number => [
    { label: 'Account', to: '/profile?tab=account' },
    { label: 'Orders', to: '/profile' },
    { label: number ? `#${number}` : 'Order' },
  ];

  if (view.status === 'loading' && !view.order) {
    return (
      <main className="pk-page pk-order">
        <div className="pk-container">
          <PageHeader title="Order details" crumbs={crumbs()} />
          <div role="status" aria-live="polite">
            <span className="pk-account__sr">Loading your order</span>
            <PageSkeleton />
          </div>
        </div>
      </main>
    );
  }

  if (view.status === 'error') {
    return (
      <main className="pk-page pk-order">
        <div className="pk-container pk-container--narrow">
          <PageHeader title="Order details" crumbs={crumbs()} />
          {view.notFound ? (
            <EmptyState
              icon={<PackageSearch />}
              title="We couldn't find this order"
              text="It may belong to a different account. Your orders are listed on your account page."
              actions={
                <Link to="/profile" className="pk-btn pk-btn--primary pk-btn--sm">
                  View my orders
                </Link>
              }
            />
          ) : (
            <EmptyState
              role="alert"
              icon={<CircleAlert />}
              title="We couldn't load this order"
              text={view.error}
              actions={
                <>
                  <button type="button" className="pk-btn pk-btn--primary pk-btn--sm" onClick={load}>
                    Retry
                  </button>
                  <Link to="/profile" className="pk-btn pk-btn--secondary pk-btn--sm">
                    View my orders
                  </Link>
                </>
              }
            />
          )}
        </div>
      </main>
    );
  }

  const { order } = view;
  const state = resolveOrderState(order);
  const meta = orderStateMeta(state);
  const number = orderNumber(order);
  const paid = isPaidState(state);
  const deliverables = (order.digitalDeliverables || []).filter(d => d?.content?.length);
  const fulfillment = order.fulfillment;
  const needsGithub = (fulfillment?.issues || []).some(issue => issue.code === 'NO_GITHUB_USERNAME');
  const hasRepository =
    deliverables.some(d => d.content.some(c => c.fileType === 'repository' || c.repositoryName)) ||
    Boolean(fulfillment?.repositoryAccessGranted) ||
    needsGithub;
  const preparing = paid && (fulfillment ? fulfillment.status !== 'Delivered' : deliverables.length === 0);

  const openItem = async (productId, itemIndex) => {
    const result = await openDeliverableItem(order.orderId, productId, itemIndex);
    if (!result.success) toast.error(result.error);
  };

  const onSettingsSaved = user => {
    applyProfileUpdate(dispatch, user);
    refresh();
  };

  return (
    <main className="pk-page pk-order">
      <div className="pk-container">
        <PageHeader
          title={`Order ${number}`}
          subtitle={`Placed ${formatDateTime(order.createdAt)}`}
          crumbs={crumbs(number)}
          aside={
            <span className="pk-badge pk-order__badge" data-tone={meta.tone}>
              {meta.label}
            </span>
          }
        />

        <div className="pk-split">
          <div className="pk-stack pk-order__main">
            <OrderItemsList items={order.items} />

            {paid && (
              <section className="pk-card" aria-labelledby="order-access-title">
                <div className="pk-card__head">
                  <h2 className="pk-card__title" id="order-access-title">
                    Your files and access
                  </h2>
                </div>
                <div className="pk-stack">
                  {preparing && (
                    <div className="pk-alert" data-tone={needsGithub ? 'warning' : 'info'} role="status">
                      {needsGithub ? <CircleAlert aria-hidden="true" /> : <Info aria-hidden="true" />}
                      <div className="pk-order__alert-body">
                        <strong>
                          {fulfillment?.status === 'PartiallyDelivered' ? 'Almost there' : 'Preparing your access'}
                        </strong>
                        <span>
                          {fulfillment?.customerMessage ||
                            "We're setting up your files and repository access. They appear on this page when ready."}
                        </span>
                        {needsGithub && (
                          <button
                            type="button"
                            className="pk-btn pk-btn--primary pk-btn--xs"
                            onClick={() => setSettingsOpen(true)}
                          >
                            Add GitHub username
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {deliverables.map(group => (
                    <div key={group.productId} className="pk-deliverables">
                      {(deliverables.length > 1 || group.edition) && (
                        <h3 className="pk-title-3">
                          {deliverables.length > 1 ? group.productName : 'Files'}
                          {group.edition && <span className="pk-deliverables__edition"> · {group.edition}</span>}
                        </h3>
                      )}
                      <ul className="pk-deliverables__list">
                        {group.content.map((item, index) => (
                          <li key={`${group.productId}-${index}`}>
                            <DeliverableCard
                              item={item}
                              expiresAt={group.accessExpiresAt}
                              onOpen={() => openItem(group.productId, index)}
                            />
                          </li>
                        ))}
                      </ul>
                      {group.accessExpiresAt && (
                        <p className="pk-hint">Access until {formatDate(group.accessExpiresAt)}.</p>
                      )}
                    </div>
                  ))}

                  {!preparing && deliverables.length === 0 && (
                    <p className="pk-order__text">
                      There are no files to download for this order. If you expected some, contact support.
                    </p>
                  )}

                  {hasRepository && (
                    <>
                      <hr className="pk-divider pk-order__divider" />
                      <RepositoryAccess order={order} onAddUsername={() => setSettingsOpen(true)} />
                    </>
                  )}
                </div>
              </section>
            )}

            {paid && <OrderReviews items={order.items} />}
          </div>

          <aside className="pk-stack pk-order__side" aria-label="Order summary">
            <PaymentStatus order={order} state={state} refreshing={refreshing} onRefresh={refresh} />
            <OrderSummary order={order} />
            <OrderHelp order={order} />
          </aside>
        </div>
      </div>

      <AccountSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSaved={onSettingsSaved}
      />
    </main>
  );
};

const OrderDetailsPage = () => (
  <OrderErrorBoundary>
    <OrderDetails />
  </OrderErrorBoundary>
);

export default OrderDetailsPage;
